import React, { useRef, useState, useEffect } from "react";
import { api } from "@/AuthContext";
import { toast } from "sonner";
import { DownloadSimple, FilePdf, MicrosoftExcelLogo, CaretDown } from "@phosphor-icons/react";

/**
 * Reusable export button with PDF/Excel options.
 * Props:
 *   - resource: "dashboard" | "documents" | "obligations" | "calendar"
 *   - label?: button label (default "Exportar")
 *   - variant?: "primary" (blue) | "ghost" (outlined)
 */
export default function ExportButton({ resource, label = "Exportar", variant = "ghost" }) {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const onClick = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  const download = async (format) => {
    setBusy(true); setOpen(false);
    try {
      const r = await api.get(`/export/${resource}.${format}`, { responseType: "blob" });
      const cd = r.headers["content-disposition"] || "";
      const m = /filename="?([^"]+)"?/.exec(cd);
      const fname = m ? m[1] : `${resource}.${format}`;
      const url = URL.createObjectURL(new Blob([r.data]));
      const a = document.createElement("a");
      a.href = url; a.download = fname; document.body.appendChild(a); a.click();
      a.remove(); URL.revokeObjectURL(url);
      toast.success(`Descargado: ${fname}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || `No se pudo exportar a ${format.toUpperCase()}`);
    } finally { setBusy(false); }
  };

  const btnClass = variant === "primary"
    ? "px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white"
    : "px-4 py-2 bg-white border border-neutral-200 hover:bg-neutral-50 text-neutral-700";

  return (
    <div className="relative inline-block" ref={ref}>
      <button
        onClick={() => setOpen(o => !o)}
        disabled={busy}
        className={`inline-flex items-center gap-2 text-sm font-semibold rounded-md transition-colors disabled:opacity-60 ${btnClass}`}
        data-testid={`export-btn-${resource}`}
      >
        <DownloadSimple size={16} weight="bold" />
        {busy ? "Generando..." : label}
        <CaretDown size={12} />
      </button>
      {open && (
        <div className="absolute right-0 mt-2 w-56 bg-white border border-neutral-200 rounded-md shadow-xl z-40 anim-fade-up" data-testid={`export-menu-${resource}`}>
          <button
            onClick={() => download("pdf")}
            className="w-full text-left px-4 py-3 hover:bg-neutral-50 flex items-center gap-3 text-sm border-b border-neutral-100"
            data-testid={`export-${resource}-pdf`}
          >
            <FilePdf size={20} weight="duotone" className="text-rose-500" />
            <div>
              <div className="font-semibold text-neutral-900">PDF</div>
              <div className="text-xs text-neutral-500">Documento ejecutivo</div>
            </div>
          </button>
          <button
            onClick={() => download("xlsx")}
            className="w-full text-left px-4 py-3 hover:bg-neutral-50 flex items-center gap-3 text-sm"
            data-testid={`export-${resource}-xlsx`}
          >
            <MicrosoftExcelLogo size={20} weight="duotone" className="text-emerald-600" />
            <div>
              <div className="font-semibold text-neutral-900">Excel</div>
              <div className="text-xs text-neutral-500">Datos completos editables</div>
            </div>
          </button>
        </div>
      )}
    </div>
  );
}
